import clsx from "clsx";
import type { PlanungAssignment, PlanungDayNote, PlanungOrderLink } from "@/lib/montageplanung";
import { AssignmentProjectActions } from "./AssignmentProjectActions";
import { splitOrderLinkLabel } from "./displayLabels";
import { WEEKDAY_SHORT, formatDateDe, parseIso } from "./dateUtils";

function formatDayNoteLabel(iso: string): string {
  const date = parseIso(iso.slice(0, 10));
  const weekday = WEEKDAY_SHORT[date.getDay()] ?? "";
  return weekday ? `${weekday}, ${formatDateDe(iso)}` : formatDateDe(iso);
}

function filledDayNotes(notes: PlanungDayNote[] | null | undefined): PlanungDayNote[] {
  return (notes ?? []).filter((day) => day.note?.trim());
}

function AssignmentOrderSummaryList({ orders }: { orders: PlanungOrderLink[] }) {
  return (
    <ul className="mt-1.5 space-y-1.5">
      {orders.map((order) => {
        const { number, details } = splitOrderLinkLabel(order.order_id, order.order_label);
        return (
          <li
            key={order.order_id}
            className="rounded-lg border border-separator-opaque bg-fill-quaternary/40 px-2.5 py-2"
          >
            <div className="font-mono text-[13px] font-bold leading-tight tracking-tight text-label tabular-nums">
              {number}
            </div>
            {details ? (
              <div className="mt-0.5 text-xs leading-snug text-label-secondary line-clamp-3 break-words">
                {details}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export interface AssignmentDetailsContentProps {
  assignment: PlanungAssignment;
  rowLabel?: string | null;
  className?: string;
  showProjectActions?: boolean;
}

export function AssignmentDetailsContent({
  assignment,
  rowLabel,
  className,
  showProjectActions = true,
}: AssignmentDetailsContentProps) {
  const orders = assignment.orders ?? [];
  const isCompanyEvent = !!assignment.is_company_event;
  const assignmentHint = assignment.note?.trim() || "";
  const dayNotes = filledDayNotes(assignment.day_notes);

  return (
    <div className={clsx("space-y-0", className)}>
      {rowLabel ? (
        <div className="mb-2 text-xs text-label-tertiary">{rowLabel}</div>
      ) : null}

      <div className="flex items-start gap-2">
        {!isCompanyEvent ? (
          <span
            className="mt-0.5 inline-block h-3 w-3 shrink-0 rounded-sm"
            style={{ backgroundColor: assignment.color || "#0a84ff" }}
            aria-hidden
          />
        ) : (
          <span
            className="mt-0.5 inline-block h-3 w-3 shrink-0 rounded-sm mp-legend-swatch--company-event"
            aria-hidden
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="font-semibold text-label">{assignment.title}</span>
            {assignment.is_series && (assignment.linked_assignment_ids?.length ?? 0) > 0 && (
              <span className="rounded border border-separator px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-label-secondary">
                Serie
              </span>
            )}
            {assignment.is_fixed && (
              <span className="rounded bg-status-red/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-status-red">
                Fix
              </span>
            )}
            {assignment.is_blocker && (
              <span className="rounded border border-dashed border-label-secondary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-label-secondary">
                Blocker
              </span>
            )}
            {isCompanyEvent && (
              <span className="rounded bg-[#ff9f0a]/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#c93400] dark:text-[#ffb340]">
                Betriebsveranstaltung
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-label-secondary">
            {formatDateDe(assignment.date_from)} – {formatDateDe(assignment.date_to)}
            {assignment.event_start_time ? ` · ${assignment.event_start_time} Uhr` : null}
          </div>
          {assignment.event_address?.trim() ? (
            <div className="mt-1 text-xs text-label-secondary">{assignment.event_address.trim()}</div>
          ) : null}
        </div>
      </div>

      {orders.length > 0 && (
        <div className="mt-2.5 border-t border-separator pt-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
            {isCompanyEvent ? "Projekte (Zeiterfassung)" : "Aufträge"}
          </div>
          {isCompanyEvent ? (
            <p className="mt-1 text-[11px] text-label-tertiary">
              Arbeitszeiten können auf diese Aufträge gebucht werden.
            </p>
          ) : null}
          {showProjectActions ? (
            <div className="mt-1.5">
              <AssignmentProjectActions orders={orders} />
            </div>
          ) : (
            <AssignmentOrderSummaryList orders={orders} />
          )}
        </div>
      )}

      {isCompanyEvent ? (
        <div className={clsx("mt-2.5 border-t border-separator pt-2", orders.length === 0 && "mt-2")}>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
            Programm / Beschreibung
          </div>
          <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-label-secondary">
            {assignmentHint || "Kein Programm hinterlegt."}
          </p>
        </div>
      ) : null}

      {!isCompanyEvent && assignmentHint ? (
        <div className={clsx("mt-2.5 border-t border-separator pt-2", orders.length === 0 && "mt-2")}>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
            Hinweis für diesen Einsatz
          </div>
          <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-label-secondary">
            {assignmentHint}
          </p>
        </div>
      ) : null}

      {!isCompanyEvent && dayNotes.length > 0 ? (
        <div
          className={clsx(
            "mt-2.5 border-t border-separator pt-2",
            orders.length === 0 && !assignmentHint && "mt-2",
          )}
        >
          <div className="text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
            Hinweise zu einzelnen Tagen
          </div>
          <ul className="mt-1.5 space-y-1.5">
            {dayNotes.map((day) => (
              <li key={day.work_date}>
                <div className="text-[11px] font-semibold text-label">{formatDayNoteLabel(day.work_date)}</div>
                <p className="whitespace-pre-wrap text-xs leading-relaxed text-label-secondary">
                  {day.note.trim()}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
